import React, { useState } from 'react';
import { Button, Checkbox, FormControlLabel, FormGroup, Typography, IconButton, Box, Paper, Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import styles from './styles.module.css';
import { useTranslation } from 'react-i18next';

const CsvExportPopup = ({ handleClose, onExport, columnMapping, title }) => {
  const { t } = useTranslation('common');

  const MANDATORY_KEYS = ['plu', 'name', 'price', 'category_name', 'type', 'unit'];

  // Initialize selected columns - default to all true
  const [selectedColumns, setSelectedColumns] = useState(
    Object.keys(columnMapping).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {})
  );

  const handleToggle = (key) => {
    if (MANDATORY_KEYS.includes(key)) return;
    setSelectedColumns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSelectAll = (select) => {
    const newSelection = {};
    Object.keys(selectedColumns).forEach((key) => {
      newSelection[key] = MANDATORY_KEYS.includes(key) ? true : select;
    });
    setSelectedColumns(newSelection);
  };

  const handleExportClick = () => {
    const filteredMapping = {};

    // Always add mandatory columns first
    MANDATORY_KEYS.forEach((key) => {
      const originalVal = columnMapping[key];
      if (originalVal) {
        const translatedTitle = getColumnLabel(key);
        if (typeof originalVal === 'object' && originalVal !== null) {
          filteredMapping[key] = { ...originalVal, title: originalVal.exportTitle || translatedTitle };
        } else {
          filteredMapping[key] = translatedTitle;
        }
      }
    });

    // Add selected non-mandatory columns
    Object.keys(selectedColumns).forEach((key) => {
      if (selectedColumns[key]) {
        const originalVal = columnMapping[key];
        const translatedTitle = getColumnLabel(key);
        if (typeof originalVal === 'object' && originalVal !== null) {
          filteredMapping[key] = { ...originalVal, title: originalVal.exportTitle || translatedTitle };
        } else {
          filteredMapping[key] = translatedTitle;
        }
      }
    });
    onExport(filteredMapping);
    handleClose();
  };

  const formatLabel = (str) => {
    if (typeof str !== 'string') return str;

    // Handle specific known overrides
    if (str.toLowerCase() === 'usp_kg_ratio') return 'USP Kg Ratio';
    if (str.toLowerCase() === 'ean') return 'EAN';
    if (str.toLowerCase() === 'plu') return 'PLU';
    if (str.toLowerCase() === 'hsn') return 'HSN';
    if (str.toLowerCase() === 'mrp') return 'MRP';
    if (
      str.toLowerCase() === 'cess' ||
      str.toLowerCase() === 'additional tax' ||
      str.toLowerCase() === 'additional_tax'
    ) {
      return 'Additional Tax / Cess';
    }

    // Replace underscores with spaces
    let formatted = str.replace(/_/g, ' ');
    // Add space before capital letters (handling camelCase)
    formatted = formatted.replace(/([a-z])([A-Z])/g, '$1 $2');
    // Capitalize first letter of each word
    formatted = formatted.replace(/\w*\S/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
    // Fix up specific acronyms that might have been title-cased
    formatted = formatted.replace(/\bId\b/g, 'ID');

    return formatted;
  };

  const getColumnLabel = (key) => {
    const val = columnMapping[key];
    const title = val?.title || val || key;
    const exportTitle = val?.exportTitle;

    // Try translating title
    const translatedTitle = t(title);
    if (translatedTitle && translatedTitle !== title) {
      if (title === 'cess' || translatedTitle.toLowerCase() === 'additional tax') {
        return 'Additional Tax / Cess';
      }
      return translatedTitle;
    }

    // Try translating key
    const translatedKey = t(key);
    if (translatedKey && translatedKey !== key) {
      if (key === 'cess' || translatedKey.toLowerCase() === 'additional tax') {
        return 'Additional Tax / Cess';
      }
      return translatedKey;
    }

    // Fallback to exportTitle or title and format nicely
    const fallback = exportTitle || title;
    return formatLabel(fallback);
  };

  const isAnySelected = Object.values(selectedColumns).some(Boolean);
  const columnKeys = Object.keys(columnMapping);

  return (
    <div className={styles.popUp} style={{ backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}>
      <Paper
        elevation={24}
        style={{
          maxWidth: '800px',
          width: '95%',
          borderRadius: '16px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #ffffff 0%, #f9f9f9 100%)',
            borderBottom: '1px solid #eee',
          }}
        >
          <Typography variant="h5" style={{ fontWeight: 700, color: '#2b2b2b' }}>
            {title || t('Export columns') || 'Export Data'}
          </Typography>
          <IconButton onClick={handleClose} size="small" style={{ backgroundColor: '#f5f5f5' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {/* Action Bar */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          padding="12px 24px"
          bgcolor="#fcfcfc"
          borderBottom="1px solid #f0f0f0"
        >
          <Typography variant="body2" color="textSecondary" style={{ fontWeight: 500 }}>
            {Object.values(selectedColumns).filter(Boolean).length +
              MANDATORY_KEYS.filter((k) => columnMapping[k]).length}{' '}
            / {columnKeys.length} {t('Columns Selected') || 'columns selected'}
          </Typography>
          <Box gap={1} display="flex">
            <Button
              size="small"
              onClick={() => handleSelectAll(true)}
              style={{ textTransform: 'none', fontWeight: 600, color: '#3f51b5' }}
            >
              {t('Select All') || 'Select All'}
            </Button>
            <Button
              size="small"
              onClick={() => handleSelectAll(false)}
              style={{ textTransform: 'none', fontWeight: 600, color: '#666' }}
            >
              {t('Deselect All') || 'Deselect All'}
            </Button>
          </Box>
        </Box>

        {/* Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1, backgroundColor: '#ffffff' }}>
          <FormGroup>
            <Grid container spacing={1}>
              {columnKeys.map((key) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <Box
                    style={{
                      padding: '4px 12px',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      backgroundColor: selectedColumns[key]
                        ? MANDATORY_KEYS.includes(key)
                          ? '#f5f5f5'
                          : '#f0f2ff'
                        : 'transparent',
                      border: `1px solid ${
                        selectedColumns[key] ? (MANDATORY_KEYS.includes(key) ? '#eeeeee' : '#c5cdff') : '#eeeeee'
                      }`,
                      marginBottom: '4px',
                      opacity: MANDATORY_KEYS.includes(key) ? 0.8 : 1,
                    }}
                  >
                    <FormControlLabel
                      style={{ width: '100%', margin: 0 }}
                      control={
                        <Checkbox
                          checked={!!selectedColumns[key]}
                          disabled={MANDATORY_KEYS.includes(key)}
                          onChange={() => handleToggle(key)}
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          style={{
                            fontWeight: 500,
                            fontSize: '0.85rem',
                            color: selectedColumns[key] && !MANDATORY_KEYS.includes(key) ? '#1a237e' : '#424242',
                          }}
                        >
                          {getColumnLabel(key)}
                        </Typography>
                      }
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </FormGroup>
        </div>

        {/* Footer */}
        <Box padding="20px 24px" display="flex" justifyContent="flex-end" bgcolor="#f9f9f9" borderTop="1px solid #eee">
          <Button
            onClick={handleClose}
            style={{
              marginRight: '12px',
              textTransform: 'none',
              fontWeight: 600,
              color: '#d81b60',
            }}
          >
            {t('Cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleExportClick}
            variant="contained"
            disabled={!isAnySelected}
            style={{
              textTransform: 'none',
              fontWeight: 600,
              padding: '8px 32px',
              borderRadius: '8px',
              backgroundColor: isAnySelected ? '#3f51b5' : '#ccc',
              color: 'white',
              boxShadow: isAnySelected ? '0 4px 12px rgba(63, 81, 181, 0.3)' : 'none',
            }}
          >
            {t('Export') || 'Export Now'}
          </Button>
        </Box>
      </Paper>
    </div>
  );
};

export default CsvExportPopup;
